import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Sparkles, Plus, Save, Trash2, ExternalLink, Eye, EyeOff, Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const EMPTY_ROW = { name: '', description: '', image_url: '', affiliate_url: '', active: true };

const AffiliateProductsPage = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(EMPTY_ROW);
  const [savingId, setSavingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/affiliate/products`, { headers: auth() });
      setRows(data || []);
    } catch (err) {
      toast({ title: 'Load failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createOne = async () => {
    if (!draft.name.trim() || !draft.affiliate_url.trim()) {
      toast({ title: 'Missing fields', description: 'Name and affiliate link are required', variant: 'destructive' });
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/api/admin/affiliate/products`, draft, { headers: auth() });
      toast({ title: 'Added', description: draft.name });
      setDraft(EMPTY_ROW);
      await load();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  const updateField = (id, key, value) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value, __dirty: true } : r)));
  };

  const saveRow = async (row) => {
    setSavingId(row.id);
    try {
      const { id, __dirty, created_at, updated_at, position, ...changes } = row;
      await axios.put(`${API}/api/admin/affiliate/products/${id}`, changes, { headers: auth() });
      toast({ title: 'Saved', description: row.name });
      await load();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSavingId(null);
    }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/affiliate/products/${row.id}`, { headers: auth() });
      toast({ title: 'Deleted', description: row.name });
      await load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const toggleActive = async (row) => {
    await axios.put(`${API}/api/admin/affiliate/products/${row.id}`, { active: !row.active }, { headers: auth() });
    await load();
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="admin-affiliate-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Sparkles size={22} className="text-amber-500" /> Recommended Products
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Single source of truth for the affiliate &quot;Recommended&quot; section on the site. Add, edit or remove products here — the homepage tile updates automatically.
        </p>
      </div>

      {/* Create new */}
      <section className="bg-white border border-slate-200 rounded-xl p-5 mb-8" data-testid="admin-affiliate-create">
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">Add a product</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            placeholder="Product name *"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            data-testid="admin-affiliate-new-name"
          />
          <Input
            placeholder="Affiliate link (https://…) *"
            value={draft.affiliate_url}
            onChange={(e) => setDraft({ ...draft, affiliate_url: e.target.value })}
            data-testid="admin-affiliate-new-url"
          />
          <Input
            placeholder="Image URL (optional)"
            value={draft.image_url}
            onChange={(e) => setDraft({ ...draft, image_url: e.target.value })}
            data-testid="admin-affiliate-new-image"
            className="md:col-span-2"
          />
          <Textarea
            placeholder="One-line description"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            data-testid="admin-affiliate-new-description"
            className="md:col-span-2"
            rows={2}
          />
        </div>
        <div className="mt-3 flex justify-end">
          <Button
            onClick={createOne}
            disabled={creating}
            data-testid="admin-affiliate-create-btn"
            className="bg-amber-500 hover:bg-amber-400 text-white"
          >
            {creating ? <Loader2 className="animate-spin mr-2" size={14} /> : <Plus size={14} className="mr-2" />}
            Add product
          </Button>
        </div>
      </section>

      {/* List */}
      <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">
        {rows.length} product{rows.length === 1 ? '' : 's'}
      </h2>
      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500 italic" data-testid="admin-affiliate-empty">
          No products yet — use the form above to add your first.
        </p>
      ) : (
        <div className="space-y-3" data-testid="admin-affiliate-list">
          {rows.map((row) => (
            <div
              key={row.id}
              className={`bg-white border rounded-xl p-4 ${row.active ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}
              data-testid={`admin-affiliate-row-${row.id}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-[80px_1fr_1fr] gap-3 items-start">
                <div className="w-20 h-20 rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center">
                  {row.image_url ? (
                    <img src={row.image_url} alt={row.name} className="w-full h-full object-cover" />
                  ) : (
                    <Sparkles size={20} className="text-slate-300" />
                  )}
                </div>
                <div className="space-y-2 min-w-0">
                  <Input
                    value={row.name}
                    onChange={(e) => updateField(row.id, 'name', e.target.value)}
                    className="font-semibold"
                    data-testid={`admin-affiliate-name-${row.id}`}
                  />
                  <Textarea
                    value={row.description || ''}
                    onChange={(e) => updateField(row.id, 'description', e.target.value)}
                    rows={2}
                    data-testid={`admin-affiliate-description-${row.id}`}
                  />
                </div>
                <div className="space-y-2 min-w-0">
                  <Input
                    value={row.image_url || ''}
                    onChange={(e) => updateField(row.id, 'image_url', e.target.value)}
                    placeholder="Image URL"
                    data-testid={`admin-affiliate-image-${row.id}`}
                  />
                  <Input
                    value={row.affiliate_url}
                    onChange={(e) => updateField(row.id, 'affiliate_url', e.target.value)}
                    placeholder="Affiliate URL"
                    data-testid={`admin-affiliate-url-${row.id}`}
                  />
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => toggleActive(row)}
                    className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded ${row.active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                    data-testid={`admin-affiliate-toggle-${row.id}`}
                  >
                    {row.active ? <><Eye size={11} /> Active</> : <><EyeOff size={11} /> Hidden</>}
                  </button>
                  <a
                    href={row.affiliate_url}
                    target="_blank"
                    rel="sponsored nofollow noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-amber-600"
                  >
                    Test link <ExternalLink size={11} />
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => deleteRow(row)}
                    data-testid={`admin-affiliate-delete-${row.id}`}
                  >
                    <Trash2 size={12} className="mr-1" /> Delete
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => saveRow(row)}
                    disabled={!row.__dirty || savingId === row.id}
                    className="bg-slate-900 hover:bg-slate-800 text-white"
                    data-testid={`admin-affiliate-save-${row.id}`}
                  >
                    {savingId === row.id ? <Loader2 className="animate-spin mr-1" size={12} /> : <Save size={12} className="mr-1" />}
                    Save
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AffiliateProductsPage;
